import { useContext, useId, type ComponentProps } from 'react'
import { Check } from 'lucide-react'
import { Button, Checkbox, Interactive, Popover } from '@highlight/ui'
import TextArea from '@renderer/components/TextArea'
import { FeedbackContext } from '../index'

interface FeedbackInputProps {
  /** Anchor for hosts with no Popover.Trigger of their own (e.g. opened from a context menu). */
  anchor?: ComponentProps<typeof Popover.Content>['anchor']
}

export function FeedbackInput({ anchor }: FeedbackInputProps = {}) {
  const {
    text,
    setText,
    type,
    handleSend,
    handleOpenChange,
    isPending,
    confirmed,
    canArchive,
    archiveAfterSubmit,
    setArchiveAfterSubmit,
  } = useContext(FeedbackContext)
  const isPositive = type === 'positive'
  const archiveCheckboxId = useId()

  return (
    <Popover.Content showArrow={false} side="top" align="end" sideOffset={8} anchor={anchor} className="w-80 p-3">
      <Interactive>
        {confirmed ? (
          <div className="flex items-center gap-2 py-1">
            <Check size={14} className="text-foreground-placeholder shrink-0" />
            <p className="text-foreground-placeholder text-xs">Thanks for your feedback!</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-foreground-placeholder text-xs">
              {isPositive ? 'What did you like?' : 'What could be improved?'}
            </p>
            <TextArea
              value={text}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  // Stop propagation so submitting feedback never reaches a staged
                  // action's (cmd/ctrl+)enter invoke handler on a parent or window (HIG-5238).
                  e.preventDefault()
                  e.stopPropagation()
                  e.currentTarget.blur()
                  handleSend()
                }
                if (e.key === 'Escape') {
                  e.stopPropagation()
                  handleOpenChange(false)
                }
              }}
              placeholder="Add a note (optional)"
              autoFocus
              autoResize
              size="medium"
              className="max-h-[120px] resize-none"
              readOnly={isPending}
            />
            <div className="flex items-center justify-between gap-3">
              {!isPositive && canArchive && (
                <div className="text-foreground-secondary flex items-center gap-2 text-xs">
                  <Checkbox
                    id={archiveCheckboxId}
                    checked={archiveAfterSubmit}
                    onCheckedChange={setArchiveAfterSubmit}
                    disabled={isPending}
                    className="size-4 rounded-[5px]"
                  >
                    <Checkbox.Indicator />
                  </Checkbox>
                  <label htmlFor={archiveCheckboxId} className="cursor-pointer">
                    Also archive this content
                  </label>
                </div>
              )}
              <Button
                size="sm"
                variant="primary"
                onClick={handleSend}
                loading={isPending}
                disabled={isPending}
                className="ml-auto"
              >
                Send
              </Button>
            </div>
          </div>
        )}
      </Interactive>
    </Popover.Content>
  )
}
